'use client'

import { BonusBadge, Button, cn, getBonusRate, Link, Tabs } from '@gyym/ui'
import { Point } from '@gyym/ui/icons'
import React from 'react'

import { useAuthUser } from '@/modules/Auth'
import { mostPopularPlanName } from '@/modules/PointSubscriptions/constants'
import { formatNumber } from '@/utils/formatNumber'

export type PricingTab = 'subscription' | 'spot'

const tabsId = 'pricing-tabs'

const tabs: { label: string; value: PricingTab }[] = [
  {
    label: '定期購入',
    value: 'subscription',
  },
  {
    label: '都度購入',
    value: 'spot',
  },
]

const bulkPurchasePath = '/me/points?purchase=true&value=12000'
const facilitiesPath = '/facilities?sortBy=distance&order=asc'

// The plan name is the contract key with the purchase modal, so it has to match
// the name the API returns for the plan exactly
const subscriptionPath = (planName: string) =>
  `/me/points?purchase=true&planName=${encodeURIComponent(planName)}`

// Unauthenticated users are sent to signup first, then back to the intended path
const resolvePath = (path: string, isLoggedIn: boolean) =>
  isLoggedIn ? path : `/signup?path=${encodeURIComponent(path)}`

type SubscriptionPlan = {
  name: string
  /** Monthly price in yen */
  price: number
  /** Points granted for the purchase itself */
  purchasePoint: number
  /** Bonus points granted on top of `purchasePoint` */
  bonusPoint: number
  description: string
}

const subscriptionPlans: SubscriptionPlan[] = [
  // TODO: バックエンドにプレミアムプランが追加されるタイミングでコメントアウトを外す。
  // 購入モーダルはプラン名の完全一致で自動選択するため、先に出すと未選択のまま開いてしまう
  // {
  //   name: 'プレミアム',
  //   price: 12000,
  //   purchasePoint: 12000,
  //   bonusPoint: 3000,
  //   description: '週2〜3回で理想のウェルネスを叶える',
  // },
  {
    name: 'アドバンス',
    price: 10000,
    purchasePoint: 10000,
    bonusPoint: 2500,
    description: '週2回から運動もケアも充実した習慣を',
  },
  {
    name: 'ベーシック',
    price: 8000,
    purchasePoint: 8000,
    bonusPoint: 2000,
    description: '週1〜2回で運動もケアもバランスよく',
  },
  {
    name: 'スタンダード',
    price: 6000,
    purchasePoint: 6000,
    bonusPoint: 1500,
    description: '週1回の理想のウェルネス習慣づくり',
  },
  {
    name: 'ライト',
    price: 4000,
    purchasePoint: 4000,
    bonusPoint: 1000,
    description: '月2〜3回で無理なく続ける習慣づくり',
  },
  {
    name: 'エントリー',
    price: 2000,
    purchasePoint: 2000,
    bonusPoint: 500,
    description: 'まずは月1〜2回でウェルネス習慣を始める',
  },
]

// Listed from the most expensive plan down, as in Figma. Sorted explicitly so
// the rule survives future additions to the literal above.
const sortedSubscriptionPlans = [...subscriptionPlans].sort(
  (a, b) => b.price - a.price,
)

// Plans are laid out in 2 columns on SP (see the card width below)
const SP_COLUMNS = 2

const PointBadge = ({
  className,
  value,
}: {
  className?: string
  value: string
}) => (
  <div
    className={cn(
      'flex flex-wrap items-center justify-center gap-x-2 bg-white px-2 py-1',
      className,
    )}
  >
    <div className="flex items-center gap-2">
      <span className="flex rounded-full bg-red-01 p-1 text-white">
        <Point />
      </span>
      <span className="text-blue-01 typography-caption1">付与ポイント</span>
    </div>
    <span className="text-red-01 typography-subhead2">{value}</span>
  </div>
)

/**
 * Point block of a subscription plan card. Stacks label / points / bonus on SP
 * and puts the label and points on one line on PC, as in Figma.
 */
const PlanPointBadge = ({
  point,
  bonusRate,
}: {
  point: number
  bonusRate: number
}) => (
  <div className="flex w-full flex-col items-center gap-1.5 bg-white p-2">
    <div className="flex flex-col items-center gap-1.5 pc:flex-row pc:gap-2">
      <div className="flex items-center gap-2">
        <span className="flex rounded-full bg-red-01 p-1 text-white">
          <Point />
        </span>
        <span className="text-blue-01 typography-caption1">付与ポイント</span>
      </div>
      <span className="text-red-01 typography-subhead2">
        {formatNumber(point)}pt
      </span>
    </div>

    {bonusRate > 0 && <BonusBadge rate={bonusRate} />}
  </div>
)

const PurchaseButton = ({
  className,
  href,
}: {
  className?: string
  href: string
}) => (
  <Button
    className={cn('mt-auto py-2', className)}
    variant="brand"
    size="sm"
    rounded
    asChild
  >
    <Link href={href}>購入する</Link>
  </Button>
)

const SubscriptionPlans = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  // Figma aligns titles across a row on SP: the card next to 人気No.1 reserves
  // the ribbon's space too. On PC the ribbon sits in the corner, so no padding.
  const rowOf = (index: number) => Math.floor(index / SP_COLUMNS)
  const labelledRows = new Set(
    sortedSubscriptionPlans.map((plan, index) =>
      plan.name === mostPopularPlanName ? rowOf(index) : -1,
    ),
  )

  return (
    // flex-wrap rather than grid so an odd number of plans keeps the last row
    // centered instead of leaving an empty slot
    <ul className="flex w-full flex-wrap justify-center gap-2">
      {sortedSubscriptionPlans.map((plan, index) => {
        const isMostPopular = plan.name === mostPopularPlanName

        return (
          <li
            key={plan.name}
            className={cn(
              'relative flex w-[calc((100%-8px)/2)] flex-col items-center gap-4 rounded-md border-solid bg-gray-06 px-4 py-6 pc:w-[calc((100%-16px)/3)] pc:p-6',
              isMostPopular
                ? 'border-2 border-red-01'
                : 'border border-gray-05',
              labelledRows.has(rowOf(index)) && 'pt-8',
            )}
          >
            {isMostPopular && (
              // Offset by the 2px border so the ribbon sits flush on the edge.
              // Top center on SP, top-left corner on PC.
              <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-b-md bg-gradient-primary px-4 py-1 text-white typography-subhead3 pc:-left-0.5 pc:translate-x-0 pc:rounded-bl-none pc:rounded-tl-md pc:px-3">
                人気No.1
              </span>
            )}

            <div className="flex w-full flex-col items-center gap-2">
              <div className="flex flex-col items-center gap-1 text-center text-blue-01">
                <h2 className="typography-headline3">{plan.name}</h2>
                <p className="flex items-end gap-1">
                  <span className="typography-subhead1">
                    {formatNumber(plan.price)}円
                  </span>
                  <span className="typography-body2">/月</span>
                </p>
              </div>

              <PlanPointBadge
                point={plan.purchasePoint + plan.bonusPoint}
                bonusRate={getBonusRate(plan.purchasePoint, plan.bonusPoint)}
              />
            </div>

            <p className="whitespace-pre-line text-center text-black typography-caption1">
              {plan.description}
            </p>

            <PurchaseButton
              className="w-full"
              href={resolvePath(subscriptionPath(plan.name), isLoggedIn)}
            />
          </li>
        )
      })}
    </ul>
  )
}

const SpotPlans = ({ isLoggedIn }: { isLoggedIn: boolean }) => (
  <ul className="flex w-full flex-col items-center rounded-md bg-gray-06 pc:flex-row pc:items-start pc:justify-center">
    <li className="flex w-full flex-col items-center gap-4 border-b border-solid border-gray-05 px-5 py-6 pc:min-w-px pc:flex-1 pc:border-b-0 pc:border-r">
      <div className="flex flex-col items-center gap-2">
        <div className="flex flex-col items-center gap-1 text-center text-blue-01">
          <h2 className="typography-headline3">まとめ買い</h2>
          <p className="flex items-end gap-1">
            <span className="typography-subhead1">12,000円</span>
            <span className="typography-body2">/回</span>
          </p>
        </div>

        <PointBadge value="12,000pt" />
      </div>

      <p className="text-center text-black typography-caption1">
        月1・2回程度ライトにはじめたい
      </p>

      <PurchaseButton
        className="w-[200px]"
        href={resolvePath(bulkPurchasePath, isLoggedIn)}
      />
    </li>

    <li className="flex w-full flex-col items-center gap-4 px-5 py-6 pc:min-w-px pc:flex-1">
      <div className="flex flex-col items-center gap-2">
        <div className="flex flex-col items-center gap-1 text-center text-blue-01">
          <h2 className="typography-headline3">都度払い</h2>
          <p className="flex items-center justify-center gap-1">
            <span className="typography-subhead1">不足分 + 手数料</span>
            <sup className="top-0 self-start text-[8px] leading-[18px] text-black">
              ※
            </sup>
            <span className="typography-body2">/ 回</span>
          </p>
        </div>

        <PointBadge value="不足購入分" />
      </div>

      <p className="text-center text-black typography-caption1">
        お試しで利用したい
      </p>

      <PurchaseButton
        className="w-[200px]"
        href={resolvePath(facilitiesPath, isLoggedIn)}
      />

      <p className="w-full text-gray-01 typography-caption2">
        ※都度払いには「少額決済手数料」が一律400円（税込）かかります。但し、定期購入サービスの加入中に追加でポイントを購入する際は少額決済手数料は一切発生しません。
      </p>
    </li>
  </ul>
)

const Notes = () => (
  <ul className="w-full text-gray-01 typography-caption1">
    <li>
      ※ポイントの
      <span className="typography-subhead3">有効期限は購入日から９０日</span>
      です。
    </li>
    <li>※支払日は、カード会社に準じます。</li>
    <li>
      ※定期購入サービスでは、毎月選択されたプランの金額がクレジットカードより定期決済され、同プランの定期購入ポイント、定期購入特典ポイントが付与されます。
    </li>
    <li>
      ※定期購入を停止する場合、その時点で保有している定期購入特典ポイントが失効となり、利用できなくなります。なお、定期購入ポイントは引き続きご利用いただけます。
    </li>
    <li>
      ※本サイトで販売するGYYMポイントについては、利用料金のお支払いが完了した後のキャンセル、払い戻しはお受け致しません。
    </li>
    <li>
      ※定期購入サービスの停止方法は
      <Link
        className="underline"
        href="https://support.gyym.jp/hc/ja/categories/29544675492505"
        target="_blank"
        external
      >
        こちら
      </Link>
      。
    </li>
  </ul>
)

export interface PricingViewProps {
  initialTab: PricingTab
}

export const PricingView = ({ initialTab }: PricingViewProps) => {
  const { isLoggedIn } = useAuthUser()
  const [tab, setTab] = React.useState<PricingTab>(initialTab)

  // The tab is local state, so sync the URL without triggering an RSC refetch
  const onTabChange = React.useCallback((value: PricingTab) => {
    setTab(value)
    window.history.replaceState(
      null,
      '',
      value === 'spot' ? '/pricing?tab=spot' : '/pricing',
    )
  }, [])

  return (
    <div className="flex flex-col gap-6 pc:gap-10">
      <Tabs
        id={tabsId}
        aria-label="ポイントの購入方法"
        tabs={tabs}
        value={tab}
        onChange={onTabChange}
      />

      <div className="flex flex-col gap-10 pc:gap-[60px]">
        <div
          role="tabpanel"
          id={`${tabsId}-panel-${tab}`}
          aria-labelledby={`${tabsId}-tab-${tab}`}
        >
          {tab === 'spot' ? (
            <SpotPlans isLoggedIn={isLoggedIn} />
          ) : (
            <SubscriptionPlans isLoggedIn={isLoggedIn} />
          )}
        </div>

        <Notes />
      </div>
    </div>
  )
}
